import { ChangeDetectionStrategy, Component, ElementRef, OnDestroy, ViewChild, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AbstractControl, FormControl, ReactiveFormsModule, ValidationErrors } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DIALOG_CONTENT_DATA, GenericDialogComponent } from '../generic-dialog';
import { PhoneMaskDirective } from '../../../directives/phone-mask.directive';
import { TippyDirective } from '../../../directives/tippy.directive';
import { AppSnackbarService } from '../../../../core/notifications/snackbar.service';
import { WhatsappMessageDialogData, WhatsappMessageDialogResult } from '../../models/whatsapp-message-dialog.types';

export type { WhatsappMessageDialogData, WhatsappMessageDialogResult } from '../../models/whatsapp-message-dialog.types';

/** Requires a Brazilian phone (DDD + 8 or 9 digits) once digits-only. */
export function phoneValidator(control: AbstractControl): ValidationErrors | null {
  const raw = String(control.value ?? '').replaceAll(/\D/g, '');
  if (!raw) return { required: true };
  return raw.length >= 10 && raw.length <= 11 ? null : { invalidPhone: true };
}

/** Builds the wa.me deep link for a Brazilian phone (digits-only, no country code) and message. */
export function buildWhatsappUrl(phoneDigits: string, message: string): string {
  return `https://wa.me/55${phoneDigits}?text=${encodeURIComponent(message)}`;
}

@Component({
  selector: 'app-whatsapp-message-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    TranslateModule,
    PhoneMaskDirective,
    TippyDirective,
  ],
  templateUrl: './whatsapp-message-dialog.component.html',
  styleUrl: './whatsapp-message-dialog.component.scss',
})
export class WhatsappMessageDialogComponent implements OnDestroy {
  private readonly dialogRef = inject(MatDialogRef<GenericDialogComponent>);
  private readonly translateService = inject(TranslateService);
  private readonly snackbarService = inject(AppSnackbarService);
  private focusTimeoutId?: ReturnType<typeof setTimeout>;
  // Support both the project's `DIALOG_CONTENT_DATA` token (used by GenericDialog)
  // and the standard `MAT_DIALOG_DATA` (used when opened directly via MatDialog.open).
  private readonly data =
    (inject(DIALOG_CONTENT_DATA, { optional: true }) as WhatsappMessageDialogData | null) ??
    (inject(MAT_DIALOG_DATA, { optional: true }) as WhatsappMessageDialogData | null);

  readonly contactName = this.data?.contactName ?? '';

  @ViewChild('phoneInput') private readonly phoneInputRef?: ElementRef<HTMLInputElement>;

  readonly phoneControl = new FormControl(this.digitsOnly(this.data?.phone ?? ''), {
    nonNullable: true,
    validators: [phoneValidator],
  });

  readonly messageControl = new FormControl(this.data?.message ?? '', { nonNullable: true });

  // Locked (chip-like, readonly) whenever a phone is already known; unlocked
  // immediately when there's none yet, so the user can type it right away.
  readonly isEditingPhone = signal(!this.phoneControl.value);

  ngOnDestroy(): void {
    clearTimeout(this.focusTimeoutId);
  }

  editPhone(): void {
    this.isEditingPhone.set(true);
    clearTimeout(this.focusTimeoutId);
    this.focusTimeoutId = setTimeout(() => {
      this.phoneInputRef?.nativeElement.focus();
      this.phoneInputRef?.nativeElement.select();
    });
  }

  openWhatsapp(): void {
    this.phoneControl.markAsTouched();

    if (this.phoneControl.invalid) {
      this.editPhone();
      return;
    }

    const phone = this.digitsOnly(this.phoneControl.value);
    const url = buildWhatsappUrl(phone, this.messageControl.value);

    const popup = window.open(url, '_blank', 'noopener');
    if (!popup) {
      this.snackbarService.warning(this.translateService.instant('whatsapp_dialog.errors.popup_blocked'));
      return;
    }

    const result: WhatsappMessageDialogResult = { phone };
    this.dialogRef.close(result);
  }

  onCancel(): void {
    this.dialogRef.close(null);
  }

  private digitsOnly(value: string): string {
    return value.replaceAll(/\D/g, '');
  }
}
